import { useEffect, useState } from 'react';

type Rgb = [number, number, number];
type Rgba = [number, number, number, number];

interface ISkeletonPalette {
  cell: Rgb;
  strong: Rgb;
  highlight: Rgb;
}

// The three greys the skeleton animations were exported with (0-1 channels), as
// Lottie stores them. Red is a matte shape and is left alone.
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const SOURCE_CELL: Rgb = [0.929, 0.933, 0.949];
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const SOURCE_STRONG: Rgb = [0.824, 0.827, 0.851];
const SOURCE_HIGHLIGHT: Rgb = [1, 1, 1];
const SOURCE_TOLERANCE = 0.002;
const CHANNEL_MAX = 255;
const SOURCE_ALPHA_INDEX = 3;
const OPAQUE = 1;
const DARK_LUMINANCE_LIMIT = 0.5;
// Rec. 709 luma weights.
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const LUMA = [0.2126, 0.7152, 0.0722];

const isSource = (value: unknown, source: Rgb): boolean =>
  Array.isArray(value) &&
  value.length >= source.length &&
  source.every((channel, index) => Math.abs(value[index] - channel) < SOURCE_TOLERANCE);

/** `top` (with alpha 0-1) painted over an opaque `under`. */
const over = (top: Rgba, under: Rgb): Rgb =>
  under.map((channel, index) =>
    Math.round(
      top[index] * top[SOURCE_ALPHA_INDEX] + channel * (1 - top[SOURCE_ALPHA_INDEX]),
    ),
  ) as Rgb;

const luminance = ([r, g, b]: Rgb): number =>
  (LUMA[0] * r + LUMA[1] * g + LUMA[2] * b) / CHANNEL_MAX;

/** Cells and header bar are `neutral` and `neutral.pressed` over the real surface. */
const buildPalette = (
  surface: Rgb,
  neutral: Rgba,
  neutralPressed: Rgba,
): ISkeletonPalette => {
  const cell = over(neutral, surface);
  const strong = over(neutralPressed, surface);
  // The shimmer sweeps lighter than the cells: plain surface on a light theme, one
  // more step of the pressed tint on a dark one.
  const highlight =
    luminance(surface) > DARK_LUMINANCE_LIMIT ? surface : over(neutralPressed, cell);
  return { cell, strong, highlight };
};

const recolorAnimation = <T>(animation: T, palette: ISkeletonPalette): T => {
  const toLottie = (rgb: Rgb): number[] => [
    ...rgb.map(channel => channel / CHANNEL_MAX),
    OPAQUE,
  ];
  const replacements: Array<[Rgb, Rgb]> = [
    [SOURCE_CELL, palette.cell],
    [SOURCE_STRONG, palette.strong],
    [SOURCE_HIGHLIGHT, palette.highlight],
  ];
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (!node || typeof node !== 'object') return node;
    const shape = node as { ty?: string; c?: { a?: number; k?: unknown } } & Record<
      string,
      unknown
    >;
    const copy: Record<string, unknown> = {};
    Object.keys(shape).forEach(key => {
      copy[key] = walk(shape[key]);
    });
    if (shape.ty === 'fl' && shape.c && !shape.c.a) {
      const match = replacements.find(([source]) => isSource(shape.c!.k, source));
      if (match) copy.c = { ...(copy.c as object), k: toLottie(match[1]) };
    }
    return copy;
  };
  return walk(animation) as T;
};

// ── Reading the theme from the page ──────────────────────────────────────────
let probeCanvas: CanvasRenderingContext2D | null | undefined;

const toRgba = (cssColor: string): Rgba | null => {
  if (probeCanvas === undefined) {
    probeCanvas =
      typeof document !== 'undefined'
        ? document.createElement('canvas').getContext('2d', { willReadFrequently: true })
        : null;
  }
  if (!probeCanvas) return null;
  probeCanvas.clearRect(0, 0, 1, 1);
  // An unparseable value is ignored and leaves the previous style in place, so start
  // from a known one and treat "unchanged" as "could not be parsed".
  probeCanvas.fillStyle = 'transparent';
  const untouched = probeCanvas.fillStyle;
  probeCanvas.fillStyle = cssColor;
  if (probeCanvas.fillStyle === untouched) return null;
  probeCanvas.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = Array.from(probeCanvas.getImageData(0, 0, 1, 1).data);
  return a === 0 ? null : [r, g, b, a / CHANNEL_MAX];
};

const resolveRole = (host: Element, variable: string): Rgba | null => {
  const probe = document.createElement('span');
  probe.style.backgroundColor = `var(${variable})`;
  host.appendChild(probe);
  const resolved = getComputedStyle(probe).backgroundColor;
  host.removeChild(probe);
  return toRgba(resolved);
};

/** The opaque colour the skeleton actually sits on: the nearest opaque ancestor. */
const findSurface = (host: Element): Rgb | null => {
  for (let el: Element | null = host; el; el = el.parentElement) {
    const colour = toRgba(getComputedStyle(el).backgroundColor);
    if (colour && colour[SOURCE_ALPHA_INDEX] === OPAQUE) {
      return [colour[0], colour[1], colour[2]];
    }
  }
  return null;
};

/** Null when the browser can't resolve the roles; callers keep the original colours. */
const readSkeletonPalette = (host: Element): ISkeletonPalette | null => {
  const surface = findSurface(host);
  const neutral = resolveRole(host, '--sk-background-neutral');
  const pressed = resolveRole(host, '--sk-background-neutral-pressed');
  return surface && neutral && pressed ? buildPalette(surface, neutral, pressed) : null;
};

/** 'light' or 'dark', following the saved choice and, for System, the OS. */
const readResolvedTheme = (): 'light' | 'dark' => {
  const chosen = document.documentElement.getAttribute('data-theme');
  if (chosen === 'light' || chosen === 'dark') return chosen;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const useResolvedTheme = (): 'light' | 'dark' => {
  const [theme, setTheme] = useState(readResolvedTheme);
  useEffect(() => {
    const update = (): void => setTheme(readResolvedTheme());
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    media?.addEventListener?.('change', update);
    return () => {
      observer.disconnect();
      media?.removeEventListener?.('change', update);
    };
  }, []);
  return theme;
};

export {
  buildPalette,
  isSource,
  luminance,
  over,
  readSkeletonPalette,
  recolorAnimation,
  SOURCE_CELL,
  SOURCE_HIGHLIGHT,
  SOURCE_STRONG,
  useResolvedTheme,
};
